import Decimal from 'decimal.js';
import { isNil } from 'lodash-es';
import { MonoTypeOperatorFunction, OperatorFunction } from 'rxjs';
import { map, tap } from 'rxjs/operators';

import {
  CardType,
  Encoding,
  FieldJustification,
  FieldType,
  ResponseDelimiter,
  ResponseKey,
} from '../constants';

import { IFieldSchema, IMessage, IMessageSchema } from './types';

function valueForType<V extends number | null | string | undefined>(
  value: V,
  type: FieldType,
  schemaValue: number | string | undefined
): string {
  if (!isNil(schemaValue) && type === FieldType.fixed) {
    return `${schemaValue}`;
  }

  if (isNil(value) || type === FieldType.reserved) {
    return '';
  }

  switch (type) {
    case FieldType.float:
      // return float representation, all dollar amounts
      // will be received as cents to avoid float imprecision
      return new Decimal(value!).dividedBy(100).toFixed(2);
    case FieldType.alphaNumeric:
    case FieldType.numeric:
    case FieldType.base64:
    default:
      // return value unchanged
      return `${value}`;
  }
}

function padValue({
  justified,
  length,
  padWith,
  value,
}: {
  justified: FieldJustification;
  length: number;
  padWith: string;
  value: string;
}): string {
  const padFunction =
    justified === FieldJustification.left ? String.prototype.padEnd : String.prototype.padStart;

  return padFunction.call(value, length, padWith);
}

// converts an object representation of a message into a
// fixed-length ascii string with fields of the appropriate
// length and with the required padding strings
function convertMessageWithSchema<S extends IMessageSchema, R extends keyof S = keyof S>(
  messageContent: IMessage<S, R>,
  messageSchema: S
): string {
  const schemaEntries: Array<[keyof S, IFieldSchema]> = Object.entries(messageSchema);

  return schemaEntries.reduce((acc, [fieldName, fieldSchema]) => {
    const fieldContent = messageContent[fieldName];

    const {
      justified = FieldJustification.left,
      length,
      padWith = ' ',
      type,
      value: schemaValue,
    } = fieldSchema;

    const value = valueForType(fieldContent, type, schemaValue);

    const paddedValue = padValue({
      justified,
      length,
      padWith,
      value,
    });

    return acc.concat(paddedValue);
  }, '');
}

export function prepare<S extends IMessageSchema, R extends keyof S>(
  messageContent: IMessage<S, R>,
  messageSchema: S
): Buffer {
  const raw = convertMessageWithSchema(messageContent, messageSchema);

  return Buffer.from(raw, Encoding.ascii);
}

export function tryParseValue(key: string, value: string): string {
  if (key === ResponseKey.CRT) {
    const cardTypeDigits: number = parseInt(value.replace(/.*\[(\d+)\]/, '$1'), 10);
    return CardType[cardTypeDigits] || value.replace(/\s+\[\d+\]/, '');
  }

  return value;
}

export type ParseResponseFunction<T> = (data: Buffer) => T;

export function parseDataUsingFunction<T>(
  parseFunction: ParseResponseFunction<T>
): OperatorFunction<Buffer, T> {
  return map(parseFunction);
}

export function reverseLookupMapForEnum<P extends object>(enumeration: P) {
  return new Map(
    Object.entries(enumeration).map(([key, value]) => [value, key] as [string, keyof P])
  );
}

export function mapKeyToReadableKey(key: string): string {
  // attempt to map key to a human-readable key name
  // fallback to the original key name if not found
  return ResponseKey[key as keyof typeof ResponseKey] || key;
}

export function sendAck<T>(
  writer: (data: Buffer) => Promise<void> | void,
  predicate: (event: T) => boolean = () => true
): MonoTypeOperatorFunction<T> {
  return tap((event: T) => {
    if (predicate(event)) {
      writer(Buffer.from(ResponseDelimiter.ACK));
    }
  });
}
